import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, Crosshair, Truck, ShieldCheck, MapPin, Award, Play, ArrowRight, Phone, ChevronDown } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_TEL, scrollToQuote } from './shared';

export const Reveal = ({ children, className = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const WHY = [
  { icon: Zap, title: 'Same/Next-Day\nService' },
  { icon: Crosshair, title: 'ADAS\nCalibration' },
  { icon: Truck, title: 'Mobile Service\nAcross Ottawa' },
  { icon: Award, title: 'Lifetime\nWarranty' },
  { icon: MapPin, title: 'Local Ottawa\nCompany' },
  { icon: ShieldCheck, title: 'Insurance\nApproved' },
];

export function WhyChoose() {
  return (
    <section data-testid="mobile-why-section" className="bg-white px-5 py-14">
      <Reveal>
        <h2 className="text-[30px] font-black tracking-[-0.02em] text-[#111827]">Why Choose A+?</h2>
      </Reveal>
      <div className="mt-7 grid grid-cols-3 gap-y-8 gap-x-2">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.05} className="flex flex-col items-center text-center">
            <motion.div whileTap={{ scale: 0.92 }} className="w-[64px] h-[64px] rounded-2xl bg-[#EAF1FD] text-[#0D5BD7] flex items-center justify-center">
              <w.icon className="w-7 h-7" strokeWidth={1.8} />
            </motion.div>
            <div className="mt-3 text-[12.5px] font-bold text-[#111827] leading-[1.3] whitespace-pre-line">{w.title}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const TEAM_IMAGE = 'https://customer-assets.emergentagent.com/job_glass-quote-hub-1/artifacts/ux9fi96v_image.png';

export function VideoSection() {
  const [note, setNote] = useState(false);
  return (
    <section data-testid="mobile-video-section" className="px-5 pb-14 bg-white">
      <Reveal>
        <button
          onClick={() => setNote(true)}
          data-testid="video-play-btn"
          className="relative w-full aspect-[4/5] rounded-3xl overflow-hidden text-left shadow-[0_20px_50px_rgba(10,18,32,0.25)] active:scale-[0.99] transition-transform"
        >
          <img src={TEAM_IMAGE} alt="The A+ Auto Glass technician team in the Ottawa shop" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '70% center' }} loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1220] via-[#0a1220]/40 to-[#0a1220]/10" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="relative w-[78px] h-[78px] rounded-full bg-white/15 border border-white/40 backdrop-blur-md flex items-center justify-center">
              <motion.span animate={{ scale: [1, 1.35], opacity: [0.5, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: 'easeOut' }} className="absolute inset-0 rounded-full border-2 border-white/60" />
              <Play className="w-8 h-8 text-white fill-white ml-1" />
            </span>
          </div>
          <div className="absolute inset-x-0 bottom-0 p-6 text-white">
            <div className="text-[11px] font-bold tracking-[0.3em] text-[#5b9bff]">WATCH OUR PROCESS</div>
            <h2 className="mt-2 text-[26px] leading-[1.05] font-black tracking-[-0.02em]">REAL WORK.<br />REAL PEOPLE.<br />REAL RESULTS.</h2>
            {note && <div className="mt-3 inline-block px-3 py-1.5 rounded-full bg-white/15 text-[12px] font-semibold" data-testid="video-coming-soon">Video coming soon</div>}
          </div>
        </button>
      </Reveal>
    </section>
  );
}

const AREAS = ['Orléans', 'Gloucester', 'Ottawa', 'Nepean', 'Kanata', 'Barrhaven', 'Rockland', 'Gatineau'];

export function ServiceArea() {
  return (
    <section data-testid="mobile-service-area" className="px-5 py-14 bg-white">
      <Reveal>
        <div className="rounded-3xl bg-[#0a1220] text-white p-7 relative overflow-hidden">
          <Truck className="absolute -right-4 -top-4 w-36 h-36 text-white/[0.06]" strokeWidth={1} />
          <div className="text-[11px] font-bold tracking-[0.3em] text-[#5b9bff]">MOBILE SERVICE</div>
          <h2 className="mt-2 text-[32px] font-black tracking-[-0.02em] leading-none">WE COME TO YOU.</h2>
          <p className="mt-3 text-[14px] text-white/75 leading-[1.55]">Mobile windshield replacement across Ottawa and surrounding areas.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {AREAS.map((a) => (
              <span key={a} className="h-8 px-3 rounded-full bg-white/10 border border-white/10 text-[12.5px] font-semibold flex items-center">{a}</span>
            ))}
            <span className="h-8 px-3 text-[12.5px] text-white/55 flex items-center">+ surrounding areas</span>
          </div>
          <motion.button whileTap={{ scale: 0.97 }} onClick={scrollToQuote} data-testid="check-area-btn" className="mt-6 w-full h-[54px] rounded-full border-2 border-white/80 text-[13px] font-bold tracking-[0.2em] flex items-center justify-center gap-3">
            CHECK MY AREA <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>
      </Reveal>
    </section>
  );
}

const OTTAWA_IMAGE = 'https://customer-assets.emergentagent.com/job_glass-quote-hub-1/artifacts/1cc3qzta_Van%20with%20Parliment.png';

export function BrandMoment() {
  return (
    <section data-testid="mobile-brand-moment" className="relative h-[420px] overflow-hidden bg-[#0a1220] text-white">
      <img src={OTTAWA_IMAGE} alt="Parliament Hill, Ottawa" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '50% 12%' }} loading="lazy" />
      <div className="absolute inset-0 bg-[#0D5BD7]/40 mix-blend-multiply" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1220] via-[#0a1220]/40 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-7">
        <Reveal>
          <span className="font-hand text-[#8bb8ff] text-[24px] leading-none -rotate-3 inline-block">Clearer roads, brighter days</span>
          <h2 className="mt-3 text-[38px] leading-[0.95] font-black tracking-[-0.02em]">DRIVE SAFE<br />WITH A+</h2>
          <p className="mt-3 text-[15px] text-white/80">Trusted glass solutions for a safer Ottawa.</p>
        </Reveal>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section data-testid="mobile-final-cta" className="bg-[#0a1220] px-5 pt-4 pb-16 text-white">
      <Reveal>
        <div className="rounded-3xl bg-[#111827] border border-white/10 p-7 text-center">
          <h2 className="text-[30px] font-black tracking-[-0.02em] leading-none">READY TO GET STARTED?</h2>
          <p className="mt-3 text-[14px] text-white/70">Get a quote now or call us directly.</p>
          <motion.button whileTap={{ scale: 0.97 }} onClick={scrollToQuote} data-testid="final-quote-btn" className="mt-6 w-full h-[58px] rounded-full bg-[#0D5BD7] text-[14px] font-bold tracking-[0.22em] flex items-center justify-center gap-3 shadow-[0_12px_30px_rgba(13,91,215,0.4)]">
            GET A QUOTE <ArrowRight className="w-5 h-5" />
          </motion.button>
          <a href={PHONE_TEL} data-testid="final-call-btn" className="mt-3 w-full h-[58px] rounded-full border-2 border-white/70 flex items-center justify-center gap-3 transition-transform active:scale-[0.97]">
            <Phone className="w-[18px] h-[18px]" /> <span className="text-[15px] font-bold tracking-wide">{PHONE_DISPLAY}</span>
          </a>
        </div>
      </Reveal>
    </section>
  );
}

const FAQS = [
  { q: 'How long does windshield replacement take?', a: 'Most replacements take 60–90 minutes, plus about an hour of safe drive-away time for the adhesive to cure.' },
  { q: 'Do you offer mobile service?', a: 'Yes. We come to your home or workplace across Ottawa, Orléans, Gatineau and surrounding areas at no extra charge.' },
  { q: 'Do you calibrate ADAS cameras?', a: 'Yes. We perform static and dynamic ADAS calibration in-house so your safety systems work exactly as the manufacturer intended.' },
  { q: 'Can you repair stone chips?', a: 'Absolutely. Chips smaller than a loonie can usually be repaired in under 30 minutes, often fully covered by insurance.' },
  { q: 'Do you provide a warranty?', a: 'Every installation is backed by our lifetime workmanship warranty against leaks and defects for as long as you own the vehicle.' },
];

export function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="mobile-faq" className="bg-white px-5 py-14">
      <Reveal><h2 className="text-[30px] font-black tracking-[-0.02em] text-[#111827]">Questions?</h2></Reveal>
      <div className="mt-6 space-y-3">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <Reveal key={f.q} delay={i * 0.04}>
              <div className={`rounded-2xl border transition-colors ${isOpen ? 'border-[#0D5BD7]/30 bg-[#F4F6FA]' : 'border-slate-200 bg-white'}`}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen} data-testid={`faq-toggle-${i}`} className="w-full flex items-center justify-between text-left px-5 py-4 gap-4">
                  <span className="text-[15px] font-bold text-[#111827] leading-snug">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#0D5BD7] shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                  <div className="overflow-hidden"><p className="px-5 pb-5 text-[14px] text-[#5F6773] leading-[1.6]">{f.a}</p></div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
